import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppSelector } from '../../redux/store/hooks';
import { useEmployee, useDeleteEmployee, useSendResetLink, useGenerateResetLink } from '../../services/httpServices/queries';
import {
  AlertCircle,
  ArrowLeft,
  Banknote,
  Briefcase,
  Building2,
  CalendarClock,
  CalendarDays,
  CreditCard,
  Edit3,
  ImageIcon,
  Mail,
  MapPin,
  Maximize2,
  MinusCircle,
  Phone,
  PlusCircle,
  Shield,
  Trash2,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';
import { ChangePasswordModal } from '../../components/ui/ChangePasswordModal';
import { ChangeUsernameModal } from '../../components/ui/ChangeUsernameModal';
import { CopyResetLinkModal } from '../../components/ui/CopyResetLinkModal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import {
  Chip,
  DetailRow,
  EmptyState,
  InfoTile,
  ProfilePhoto,
  ProfileSkeleton,
  SectionCard,
  StatTile,
  StatusPill,
  TabBar,
  UserAccountCard,
  type ProfileTab,
} from '../../components/ui/profile-ui';

type EmployeeTab = 'overview' | 'payroll' | 'documents' | 'account';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const money = (value?: number | string | null) => `৳ ${Number(value || 0).toLocaleString()}`;

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';

const titleCase = (value?: string | null) =>
  value
    ? value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : '—';

export function EmployeeView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAppSelector((state) => state.auth);
  const { data, isLoading, error } = useEmployee(id || '');
  const deleteEmployee = useDeleteEmployee();
  const sendResetLink = useSendResetLink();
  const generateResetLink = useGenerateResetLink();
  const [tab, setTab] = useState<EmployeeTab>('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showResetLinkModal, setShowResetLinkModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);

  const employee = data?.data;

  const isSelf =
    !!user?.id && !!employee && (employee.userId === user.id || employee.user?.id === user.id);

  const handleDelete = async () => {
    if (!id) return;
    if (isSelf) {
      toast.error('You cannot delete your own account while logged in.');
      return;
    }
    try {
      await deleteEmployee.mutateAsync(id);
      navigate('/employees');
    } catch {
      // handled by mutation
    }
  };

  const handleCopyResetLink = async (): Promise<string | null> => {
    if (!employee?.user?.id) return null;
    try {
      const res = await generateResetLink.mutateAsync(employee.user.id);
      const url = res?.data?.url;
      if (!url) throw new Error('No reset link returned');
      return url;
    } catch {
      toast.error('Failed to generate reset link');
      return null;
    }
  };

  const handleResendResetLink = async () => {
    if (!employee?.user?.id) return;
    try {
      await sendResetLink.mutateAsync(employee.user.id);
      toast.success(`Password reset link sent to ${employee.user.email || employee.user.username}`);
      setShowResendModal(false);
    } catch {
      toast.error('Failed to send reset link');
    }
  };

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  if (error || !employee) {
    return (
      <div className="flex w-full flex-col items-center gap-2 rounded-2xl border border-danger/20 bg-danger/5 p-8 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-danger/10 text-danger">
          <AlertCircle size={20} />
        </span>
        <p className="text-sm font-semibold text-danger">Failed to load employee details</p>
        <p className="max-w-sm text-xs text-text-muted">
          The employee may have been removed, or the server is unavailable. Please try again.
        </p>
        <button
          type="button"
          onClick={() => navigate('/employees')}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text transition-colors hover:border-primary/40"
        >
          <ArrowLeft size={14} />
          Back to Employees
        </button>
      </div>
    );
  }

  const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.name;
  const allowanceItems = employee.allowanceItems || [];
  const allowanceTotal = allowanceItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const tax = Number(employee.taxDeduction) || 0;
  const mainSalary = (Number(employee.basicSalary) || 0) + allowanceTotal - tax;
  const dailyRate =
    Number(employee.basicSalary) > 0 && Number(employee.workingDaysPerMonth) > 0
      ? Number(employee.basicSalary) / Number(employee.workingDaysPerMonth)
      : null;
  const offDays = Array.isArray(employee.weeklyOffDays)
    ? employee.weeklyOffDays.map((day) => WEEKDAYS[day]).filter(Boolean)
    : [];
  const documentCount =
    (employee.nationalIdFrontUrl ? 1 : 0) +
    (employee.nationalIdBackUrl ? 1 : 0) +
    (employee.signatureUrl ? 1 : 0);

  const tabs: ProfileTab<EmployeeTab>[] = [
    { id: 'overview', label: 'Overview', icon: UserRound },
    { id: 'payroll', label: 'Payroll', icon: Wallet },
    { id: 'documents', label: 'Documents', icon: ImageIcon, count: documentCount },
    { id: 'account', label: 'Account', icon: Shield },
  ];

  return (
    <div className="w-full space-y-4">
      {/* Compact toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => navigate('/employees')}
            aria-label="Back to employees"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-border/70 bg-surface text-text-muted shadow-sm transition-colors hover:border-primary/40 hover:text-text"
          >
            <ArrowLeft size={17} />
          </button>
          <div>
            <p className="text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
              HR Mgmt. / Employees
            </p>
            <p className="text-sm font-semibold text-text">Employee profile</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isSelf && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              aria-label="Delete employee"
              title="Delete employee"
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-danger/20 bg-danger/5 text-danger transition-colors hover:bg-danger/10"
            >
              <Trash2 size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(`/employees/${id}/edit`)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-[0.8125rem] font-semibold text-white shadow-sm transition-colors hover:bg-primary-dark"
          >
            <Edit3 size={15} />
            Edit
          </button>
        </div>
      </div>

      {/* Profile hero */}
      <section className="relative overflow-hidden rounded-2xl border border-border/70 bg-surface shadow-sm">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 left-1/3 h-56 w-56 rounded-full bg-secondary/10 blur-3xl" />

        <div className="relative flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center">
          <ProfilePhoto src={employee.photoUrl} name={fullName} active={employee.isActive} />

          <div className="min-w-0 flex-1 space-y-3.5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-bold tracking-tight text-text sm:text-2xl">{fullName}</h1>
                  <StatusPill active={employee.isActive} />
                </div>
                <p className="mt-0.5 text-[0.8125rem] text-text-muted">
                  {employee.designation?.name || 'No designation'}
                  {employee.department?.name && (
                    <span className="text-text-muted"> · {employee.department.name}</span>
                  )}
                </p>
                {employee.specializations && employee.specializations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {employee.specializations.map((specialization) => (
                      <Chip key={specialization.id}>{specialization.label}</Chip>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              <InfoTile icon={Users} label="Employee code" value={employee.employeeCode} mono />
              <InfoTile icon={Building2} label="Department" value={employee.department?.name || '—'} />
              <InfoTile icon={Briefcase} label="Designation" value={employee.designation?.name || '—'} />
              <InfoTile icon={CalendarDays} label="Joined" value={formatDate(employee.joiningDate)} />
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[0.75rem] text-text-muted">
              {employee.mobileNumber && (
                <a
                  href={`tel:${employee.mobileNumber}`}
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-text"
                >
                  <Phone size={13} />
                  {employee.mobileNumber}
                </a>
              )}
              {employee.email && (
                <a
                  href={`mailto:${employee.email}`}
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-text"
                >
                  <Mail size={13} />
                  {employee.email}
                </a>
              )}
              {employee.address && (
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <MapPin size={13} className="shrink-0" />
                  <span className="truncate">{employee.address}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Salary snapshot */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile icon={Wallet} label="Gross salary" value={money(employee.basicSalary)} hint="Basic pay" />
        <StatTile
          icon={PlusCircle}
          label="Allowances"
          value={money(allowanceTotal)}
          hint={`${allowanceItems.length} item${allowanceItems.length === 1 ? '' : 's'}`}
        />
        <StatTile
          icon={MinusCircle}
          label="Tax deduction"
          value={money(tax)}
          hint={tax > 0 ? 'Monthly deduction' : 'No tax configured'}
        />
        <StatTile
          icon={Banknote}
          label="Net salary"
          value={money(mainSalary)}
          hint={employee.payrollEnabled !== false ? 'Payroll enabled' : 'Payroll disabled'}
          tone="accent"
        />
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TabBar tabs={tabs} active={tab} onChange={setTab} />
        <p className="hidden text-[0.6875rem] text-text-muted sm:block">
          Updated {formatDate(employee.updatedAt || employee.createdAt)}
        </p>
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard
            icon={UserRound}
            title="Personal information"
            subtitle="Basic profile and contact details"
          >
            <div className="grid gap-x-8 sm:grid-cols-2 xl:grid-cols-1">
              <DetailRow label="Full name" value={fullName} />
              <DetailRow label="Gender" value={titleCase(employee.gender)} />
              <DetailRow label="Date of birth" value={formatDate(employee.dateOfBirth)} />
              <DetailRow label="Phone" value={employee.mobileNumber || '—'} />
              <DetailRow label="Email" value={employee.email || '—'} />
              <DetailRow
                label="Address"
                value={employee.address || '—'}
                className="sm:col-span-2 xl:col-span-1"
              />
            </div>
          </SectionCard>

          <SectionCard
            icon={Briefcase}
            title="Employment"
            subtitle="Role, department and working schedule"
          >
            <div className="grid gap-x-8 sm:grid-cols-2 xl:grid-cols-1">
              <DetailRow label="Employee code" value={employee.employeeCode} mono />
              <DetailRow label="Department" value={employee.department?.name || '—'} />
              <DetailRow label="Designation" value={employee.designation?.name || '—'} />
              <DetailRow label="Employment type" value={titleCase(employee.employmentType)} />
              <DetailRow label="Joining date" value={formatDate(employee.joiningDate)} />
              <DetailRow label="Weekly off days" value={offDays.length ? offDays.join(', ') : 'Policy default'} />
              <DetailRow
                label="Working days / month"
                value={Number(employee.workingDaysPerMonth) > 0 ? String(employee.workingDaysPerMonth) : 'Policy default'}
                className="sm:col-span-2 xl:col-span-1"
              />
            </div>
          </SectionCard>
        </div>
      )}

      {tab === 'payroll' && (
        <div className="grid gap-4 xl:grid-cols-3">
          <SectionCard
            icon={Wallet}
            title="Salary breakdown"
            subtitle="Monthly earnings, allowances and deductions"
            className="xl:col-span-2"
          >
            <div className="grid gap-x-8 sm:grid-cols-2">
              <DetailRow label="Basic / gross salary" value={money(employee.basicSalary)} />
              <DetailRow
                label="Allowances"
                value={<span className="text-success">+ {money(allowanceTotal)}</span>}
              />
              <DetailRow
                label="Tax deduction"
                value={<span className="text-danger">− {money(tax)}</span>}
              />
              <DetailRow
                label="Net / main salary"
                value={<span className="font-semibold text-primary">{money(mainSalary)}</span>}
              />
              <DetailRow
                label="Compensation type"
                value={titleCase(employee.compensationType || 'Monthly')}
              />
              <DetailRow
                label="Payroll status"
                value={
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold ${
                      employee.payrollEnabled !== false
                        ? 'bg-success/10 text-success'
                        : 'bg-border/60 text-text-muted'
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        employee.payrollEnabled !== false ? 'bg-success' : 'bg-text-muted'
                      }`}
                    />
                    {employee.payrollEnabled !== false ? 'Enabled' : 'Disabled'}
                  </span>
                }
              />
            </div>

            <div className="mt-4">
              <p className="mb-2 text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                Allowance breakdown
              </p>
              {allowanceItems.length > 0 ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  {allowanceItems.map((item, index) => (
                    <div
                      key={`${item.label}-${index}`}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-background/60 px-3 py-2"
                    >
                      <span className="truncate text-[0.75rem] text-text-muted">{item.label}</span>
                      <span className="shrink-0 text-[0.8125rem] font-medium tabular-nums text-text">
                        {money(item.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="rounded-lg border border-dashed border-border bg-background/50 px-3 py-3 text-center text-[0.75rem] text-text-muted">
                  No allowances configured.
                </p>
              )}
            </div>
          </SectionCard>

          <SectionCard
            icon={CalendarClock}
            title="Payroll settings"
            subtitle="Policy and attendance basis"
          >
            <div className="space-y-0">
              <DetailRow
                label="Payroll policy"
                value={employee.payrollPolicy?.name || 'Inherited (department / default)'}
              />
              <DetailRow
                label="Weekly off days"
                value={offDays.length ? offDays.join(', ') : 'Policy default'}
              />
              <DetailRow
                label="Working days / month"
                value={Number(employee.workingDaysPerMonth) > 0 ? String(employee.workingDaysPerMonth) : 'Policy default'}
              />
              <DetailRow
                label="Daily rate"
                value={dailyRate !== null ? money(Number(dailyRate.toFixed(2))) : '—'}
              />
              <DetailRow label="Revenue share" value={employee.revenueShareEligible ? 'Eligible' : 'Not eligible'} />
            </div>
          </SectionCard>
        </div>
      )}

      {tab === 'documents' && (
        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCard icon={CreditCard} title="Bank details" subtitle="Salary disbursement information">
            <div className="space-y-0">
              <DetailRow label="Bank name" value={employee.bankName || '—'} />
              <DetailRow label="Account number" value={employee.bankAccountNumber || '—'} mono />
              <DetailRow
                label="Revenue share eligible"
                value={
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold ${
                      employee.revenueShareEligible
                        ? 'bg-success/10 text-success'
                        : 'bg-border/60 text-text-muted'
                    }`}
                  >
                    {employee.revenueShareEligible ? 'Yes' : 'No'}
                  </span>
                }
              />
              {Number(employee.revenueSharePercent) > 0 && (
                <DetailRow label="Revenue share percent" value={`${employee.revenueSharePercent}%`} />
              )}
            </div>
          </SectionCard>

          <SectionCard
            icon={ImageIcon}
            title="Identity documents"
            subtitle="Click a document to open it full size"
          >
            {documentCount === 0 ? (
              <EmptyState
                icon={ImageIcon}
                title="No documents uploaded"
                hint="NID and signature images will appear here once uploaded."
              />
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {employee.nationalIdFrontUrl && (
                  <a
                    href={employee.nationalIdFrontUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="group relative overflow-hidden rounded-xl border border-border/70 bg-background"
                  >
                    <img
                      src={employee.nationalIdFrontUrl}
                      alt="NID front"
                      className="h-36 w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                    />
                    <span className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/60 to-transparent px-2.5 pb-2 pt-6 text-[0.6875rem] font-medium text-white">
                      NID front
                      <Maximize2 size={12} />
                    </span>
                  </a>
                )}
                {employee.nationalIdBackUrl && (
                  <a
                    href={employee.nationalIdBackUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="group relative overflow-hidden rounded-xl border border-border/70 bg-background"
                  >
                    <img
                      src={employee.nationalIdBackUrl}
                      alt="NID back"
                      className="h-36 w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                    />
                    <span className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/60 to-transparent px-2.5 pb-2 pt-6 text-[0.6875rem] font-medium text-white">
                      NID back
                      <Maximize2 size={12} />
                    </span>
                  </a>
                )}
                {employee.signatureUrl && (
                  <a
                    href={employee.signatureUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="group relative col-span-2 overflow-hidden rounded-xl border border-border/70 bg-white p-3"
                  >
                    <img
                      src={employee.signatureUrl}
                      alt="Employee signature"
                      className="h-24 w-full object-contain transition duration-300 group-hover:scale-[1.02]"
                    />
                    <span className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/50 to-transparent px-2.5 pb-2 pt-5 text-[0.6875rem] font-medium text-white">
                      Signature
                      <Maximize2 size={12} />
                    </span>
                  </a>
                )}
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {tab === 'account' && (
        <>
          {employee.user ? (
            <UserAccountCard
              user={employee.user}
              resending={sendResetLink.isPending}
              generating={generateResetLink.isPending}
              onResendLink={() => setShowResendModal(true)}
              onCopyResetLink={() => setShowResetLinkModal(true)}
              onChangeUsername={() => setShowUsernameModal(true)}
              onChangePassword={() => setShowPasswordModal(true)}
              onManageRoles={() => navigate(`/users/${employee.user!.id}/roles`)}
            />
          ) : (
            <SectionCard icon={Shield} title="System user account" subtitle="Sign-in identity and access">
              <EmptyState
                icon={UserRound}
                title="No user account linked"
                hint="Edit this employee to create a sign-in account and assign roles."
              />
            </SectionCard>
          )}
        </>
      )}

      {/* Modals */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete employee?"
        description={
          <>
            Are you sure you want to delete <strong className="text-text">{fullName}</strong>? This
            action cannot be undone.
          </>
        }
        icon={<Trash2 size={18} />}
        confirmLabel="Delete employee"
        tone="danger"
        loading={deleteEmployee.isPending}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteConfirm(false)}
      />

      {showUsernameModal && employee.user && (
        <ChangeUsernameModal
          user={employee.user}
          onClose={() => setShowUsernameModal(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['employees', id] })}
        />
      )}

      {showPasswordModal && employee.user && (
        <ChangePasswordModal user={employee.user} onClose={() => setShowPasswordModal(false)} />
      )}

      <CopyResetLinkModal
        open={showResetLinkModal}
        userName={fullName}
        userEmail={employee.user?.email}
        onConfirm={handleCopyResetLink}
        onClose={() => setShowResetLinkModal(false)}
      />

      <ConfirmDialog
        open={showResendModal}
        title="Resend password reset link?"
        description={
          <>
            A password reset link will be emailed to{' '}
            <span className="font-medium text-text">{employee.user?.email}</span>.
          </>
        }
        icon={<Mail size={18} />}
        confirmLabel="Send link"
        loading={sendResetLink.isPending}
        onConfirm={handleResendResetLink}
        onClose={() => setShowResendModal(false)}
      />
    </div>
  );
}
